import {
  addToast,
  Button,
  SuccessToast,
  Toastß,
} from '@otter-ai/den-components';
import { GoogleIcon, WarningTriangleIcon } from '@otter-ai/den-icons';
import { Screen } from '@otter-ai/telemetry';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { SECOND_MS } from '@/constants/time';
import { FormattedText } from '@/shared/components/FormattedText';
import { useGetCurrentUser } from '@/shared/hooks/current-user/useGetCurrentUser';
import { useAuthPopupErrorMessages } from '@/shared/hooks/useAuthPopupErrorMessages';
import { useGoogleCalendar } from '@/shared/hooks/useGoogleCalendar';
import { BuiltInApps, type SyncedAccount } from '@/shared/types/apps';
import { descriptionFormatRules } from '../../shared/utils/text';
import { AppRowLayout } from '../components/AppRowLayout';
import { useDisconnectApp } from '../hooks/useDisconnectApp';
import { useSyncedAccounts } from '../hooks/useSyncedAccounts';
import { GOOGLE_TEST_IDS } from '../test-ids/google';
import { trackGoogle } from '../tracking/google-tracking';
import { DisconnectedButton } from './DisconnectedButton';

/**
 * The row for Google on the apps settings page.
 *
 * [Figma](https://www.figma.com/design/jEoBjkGRTOV4ETZ4Qli3Bl/Account-Settings-React?node-id=63-6505&m=dev)
 */
export function GoogleAppRow() {
  const t = useTranslations('settings:integrations.apps.google');
  const { isLoading: loadingConnectedAccount } = useSyncedAccounts({
    service: BuiltInApps.GOOGLE,
  });
  const { userId } = useGetCurrentUser();
  const { connectGoogleCalendar } = useGoogleCalendar({
    userid: userId,
    screen: Screen.apps,
    requestedPermissions: {
      contacts: true,
    },
  });
  const { disconnectAppAsync } = useDisconnectApp();
  const { accounts } = useSyncedAccounts({ service: BuiltInApps.GOOGLE });
  // const isConnecting = useAtomValue(connectingAppAtom('google'));
  const [_isAuthWindowOpen, setIsAuthWindowOpen] = useState(false);
  const { showAuthPopupErrorToast } = useAuthPopupErrorMessages('Google');

  const onClickAdd = async () => {
    trackGoogle.main.connectGoogle();
    setIsAuthWindowOpen(true);
    try {
      await connectGoogleCalendar();
    } catch (err) {
      showAuthPopupErrorToast(err);
    }
    setTimeout(() => {
      setIsAuthWindowOpen(false);
    }, SECOND_MS);
  };

  const handleDisconnect = async (account: SyncedAccount) => {
    trackGoogle.main.disconnectGoogle();
    try {
      // In the case of multiple accounts, we need to disconnect each one.
      await disconnectAppAsync({ service: BuiltInApps.GOOGLE, account });
      addToast(<SuccessToast description={t('successfulDisconnected')} />);
      return true;
    } catch (err) {
      displayDisconnectionErrorToastForError(err, t);
      return false;
    }
  };

  const description = (
    <FormattedText>
      {t.rich('description', descriptionFormatRules({}))}
    </FormattedText>
  );
  return (
    <AppRowLayout
      appRowId="google-app"
      Icon={GoogleIcon}
      title="Google"
      description={description}
      buttons={
        // Multiple Google accounts are supported
        <Button
          disabled={loadingConnectedAccount}
          onClick={loadingConnectedAccount ? undefined : onClickAdd}
          data-testid={GOOGLE_TEST_IDS.addButton}
        >
          {t('addButton')}
        </Button>
      }
      linkedAccounts={accounts.map((account) => ({
        accountName: account.account,
        accountId: account.account,
        button: (
          <DisconnectedButton
            disconnectButtonText={t('disconnectButton')}
            disconnectAction={() => handleDisconnect(account)}
            tooltipCaption={t('disconnectTooltip', {
              accountName: account.account,
            })}
            data-testid={GOOGLE_TEST_IDS.disconnectButton}
          />
        ),
      }))}
    />
  );
}

function displayDisconnectionErrorToastForError(
  _error: unknown,
  t: ReturnType<typeof useTranslations>,
) {
  // TODO(sleahy): User the error to display a more specific toast.
  addToast(
    <Toastß
      altText=""
      description={t('failedDisconnect')}
      icon={<WarningTriangleIcon />}
    />,
  );
}
